import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "../../components/shared/Button/Button";
import Input from "../../components/shared/Input/Input";
import { useUpdateCollection } from "./hooks/useUpdateCollection";
import {
  COLLECTION_NAME_MIN_LENGTH,
  COLLECTION_NAME_MAX_LENGTH,
  COLLECTION_NAME_PATTERN,
} from "./constants/collectionsJoinSymbolUrl";
import { useAllSwingCollectionsByUserId } from "./hooks/useAllSwingCollectionsByUserId";

const normalizeName = (value = "") => value.trim().toLowerCase();

const baseRenameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(
      COLLECTION_NAME_MIN_LENGTH,
      `Minimum ${COLLECTION_NAME_MIN_LENGTH} characters`,
    )
    .max(
      COLLECTION_NAME_MAX_LENGTH,
      `Maximum ${COLLECTION_NAME_MAX_LENGTH} characters`,
    )
    .regex(
      COLLECTION_NAME_PATTERN,
      "Only letters, numbers, spaces and _ - ( ) .",
    ),
});

export function InputRenameCollection({
  collection,
  onCancel,
  onSubmit,
  size,
}) {
  const { collections } = useAllSwingCollectionsByUserId();

  const updateCollectionMutation = useUpdateCollection();

  const normalizedNames = useMemo(() => {
    return new Set(
      (collections || [])
        .filter((item) => item.id !== collection.id)
        .map((item) => normalizeName(item.collectionName)),
    );
  }, [collections, collection.id]);

  const renameSchema = useMemo(() => {
    return baseRenameSchema.refine(
      ({ name }) => !normalizedNames.has(normalizeName(name)),
      {
        message: "Collection name already exists",
        path: ["name"],
      },
    );
  }, [normalizedNames]);

  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = useForm({
    defaultValues: { name: collection.collectionName },
    mode: "onChange",
    resolver: zodResolver(renameSchema),
  });

  const handleRenameSubmit = async ({ name }) => {
    if (name.trim() !== collection.collectionName) {
      await updateCollectionMutation.mutateAsync({
        collectionId: collection.id,
        collectionName: name,
        videoIds: collection.videoIds || [],
      });
      if (onSubmit) onSubmit();
    } else {
      onCancel();
    }
  };

  return (
    <div className="flex flex-wrap items-start gap-2">
      <div className="min-w-36 flex-1">
        <Input
          autoFocus
          className="text-sm"
          control={control}
          isDisabled={updateCollectionMutation.isPending}
          name="name"
          size={size}
        />
      </div>

      <div className="shrink-0 grid grid-cols-2 gap-1">
        <Button
          className="w-full text-sm"
          color="primary500"
          isDisabled={updateCollectionMutation.isPending || !isDirty}
          onClick={handleSubmit(handleRenameSubmit)}
          size={size}
        >
          Save
        </Button>

        <Button
          className="w-full text-sm"
          color="gray100"
          isDisabled={updateCollectionMutation.isPending}
          onClick={(e) => {
            e?.stopPropagation();
            onCancel();
          }}
          size={size}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
